import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/firebase';

export type AdminThemeSettings = {
  global: {
    headingColor: string;
    bodyColor: string;
    headingWeight: number;
  };
  pages: {
    analysis: {
      pageTitleSize: string;
      sectionTitleSize: string;
      kpiValueSize: string;
      accentColor: string;
    };
  };
  updatedAt?: Date;
  updatedBy?: string;
  schemaVersion?: number;
};

export const DEFAULT_ADMIN_THEME_SETTINGS: AdminThemeSettings = {
  global: {
    headingColor: '#0f172a',
    bodyColor: '#64748b',
    headingWeight: 700,
  },
  pages: {
    analysis: {
      pageTitleSize: 'clamp(1.25rem,2.8vw,2.25rem)',
      sectionTitleSize: 'clamp(1.1rem,2.4vw,1.85rem)',
      kpiValueSize: 'clamp(1.75rem,3.4vw,3rem)',
      accentColor: '#2563eb',
    },
  },
  schemaVersion: 1,
};

const settingsDocRef = () => doc(db, 'SystemSettings', 'adminTheme');

export const getAdminThemeSettings = async (): Promise<AdminThemeSettings> => {
  const snapshot = await getDoc(settingsDocRef());
  const data = snapshot.exists() ? (snapshot.data() as Partial<AdminThemeSettings>) : {};

  return {
    ...DEFAULT_ADMIN_THEME_SETTINGS,
    ...data,
    global: {
      ...DEFAULT_ADMIN_THEME_SETTINGS.global,
      ...(data.global || {}),
    },
    pages: {
      ...DEFAULT_ADMIN_THEME_SETTINGS.pages,
      ...(data.pages || {}),
      analysis: {
        ...DEFAULT_ADMIN_THEME_SETTINGS.pages.analysis,
        ...(data.pages?.analysis || {}),
      },
    },
  };
};

export const saveAdminThemeSettings = async (settings: AdminThemeSettings, updatedBy?: string) => {
  await setDoc(
    settingsDocRef(),
    {
      ...settings,
      updatedBy: updatedBy || null,
      updatedAt: new Date(),
      schemaVersion: 1,
    },
    { merge: true }
  );
};

export const applyAdminThemeSettingsToDocument = (settings: AdminThemeSettings) => {
  const root = document.documentElement;

  root.style.setProperty('--admin-color-heading', settings.global.headingColor);
  root.style.setProperty('--admin-color-body', settings.global.bodyColor);
  root.style.setProperty('--admin-fw-heading', `${settings.global.headingWeight}`);

  root.style.setProperty('--admin-analysis-title-size', settings.pages.analysis.pageTitleSize);
  root.style.setProperty('--admin-analysis-section-title-size', settings.pages.analysis.sectionTitleSize);
  root.style.setProperty('--admin-analysis-kpi-size', settings.pages.analysis.kpiValueSize);
  root.style.setProperty('--admin-analysis-accent-color', settings.pages.analysis.accentColor);
};
